import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CreditCard, Building2, Banknote, FileText } from "lucide-react";

interface PaymentMethodSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopSettings: any;
  itemName: string;
  itemPrice: number;
  onPaymentMethodSelected: (method: string) => void;
}

export function PaymentMethodSelector({
  open,
  onOpenChange,
  shopSettings,
  itemName,
  itemPrice,
  onPaymentMethodSelected
}: PaymentMethodSelectorProps) {
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);

  const paymentMethods = [
    {
      id: 'stripe',
      name: 'Card Payment',
      icon: CreditCard,
      enabled: shopSettings?.stripe_enabled,
      description: 'Pay securely with credit or debit card'
    },
    {
      id: 'bacs',
      name: 'Bank Transfer (BACS)',
      icon: Building2,
      enabled: shopSettings?.bacs_enabled,
      description: 'Direct bank transfer',
      details: shopSettings?.bacs_enabled ? (
        <div className="mt-2 text-sm text-muted-foreground">
          <p>Account Number: {shopSettings.bacs_account_number}</p>
          <p>Sort Code: {shopSettings.bacs_sort_code}</p>
          <p className="mt-1">Reference: {itemName}</p>
        </div>
      ) : null
    },
    {
      id: 'cash',
      name: 'Cash Payment',
      icon: Banknote,
      enabled: shopSettings?.cash_enabled,
      description: 'Pay with cash',
      details: shopSettings?.cash_instructions ? (
        <div className="mt-2 text-sm text-muted-foreground">
          <p>{shopSettings.cash_instructions}</p>
        </div>
      ) : null
    },
    {
      id: 'cheque',
      name: 'Cheque',
      icon: FileText,
      enabled: shopSettings?.cheque_enabled,
      description: 'Pay by cheque',
      details: shopSettings?.cheque_enabled ? (
        <div className="mt-2 text-sm text-muted-foreground">
          <p>Payable to: {shopSettings.cheque_payable_to}</p>
          <p>Address: {shopSettings.cheque_address}</p>
        </div>
      ) : null
    }
  ];

  const enabledMethods = paymentMethods.filter(m => m.enabled);

  if (enabledMethods.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment Not Available</DialogTitle>
          </DialogHeader>
          <p>No payment methods are currently configured. Please contact the seller directly using the contact method provided.</p>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Select Payment Method</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-center p-4 bg-muted rounded-lg">
            <p className="font-semibold">{itemName}</p>
            <p className="text-2xl font-bold text-primary">£{itemPrice.toFixed(2)}</p>
          </div>

          <div className="grid gap-3">
            {enabledMethods.map((method) => {
              const Icon = method.icon;
              return (
                <Card 
                  key={method.id}
                  className={`cursor-pointer transition-all ${
                    selectedMethod === method.id ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => setSelectedMethod(method.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Icon className="h-6 w-6 text-primary mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold">{method.name}</h3>
                        <p className="text-sm text-muted-foreground">{method.description}</p>
                        {method.details}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                if (selectedMethod) {
                  onPaymentMethodSelected(selectedMethod);
                  onOpenChange(false);
                }
              }}
              disabled={!selectedMethod}
              className="flex-1"
            >
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}